// src/components/Navigation.js
import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, Menu, X, LogOut } from 'lucide-react';
import Brand from './Brand';

const Navigation = ({ mobileMenuOpen, setMobileMenuOpen }) => {
  const [scrolled, setScrolled] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Check authentication status
    const checkAuth = () => {
      const authStatus = localStorage.getItem('isAuthenticated');
      setIsAuthenticated(authStatus === 'true');
    };

    checkAuth();
    // Listen for storage changes (for logout from other tabs)
    window.addEventListener('storage', checkAuth);

    return () => window.removeEventListener('storage', checkAuth);
  }, [location]);

  const navItems = [
    { path: '/', label: 'Home' },
    { path: '/product', label: 'Product' },
    { path: '/solutions', label: 'Solutions' },
    { path: '/pricing', label: 'Pricing' },
    { path: '/demo', label: 'Demo' },
    { path: '/resources', label: 'Resources' },
    { path: '/about', label: 'About' },
    { path: '/contact', label: 'Contact' }
  ];

  const handleDashboardClick = () => {
    if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      navigate('/login');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('currentUser');
    localStorage.removeItem('loginTime');
    navigate('/login');
  };

  const isDashboardPage = location.pathname === '/dashboard';
  const showLogoutButton = isDashboardPage && isAuthenticated;


  return (
    <nav aria-label="Main" className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-lg' : 'bg-white/95 backdrop-blur'}`}>
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-20">
          <Brand as={NavLink} to="/" theme="light" markClassName="text-blue-600"
            className="text-2xl font-bold text-gray-900" />

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center space-x-6">
            {navItems.map(item => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `font-medium transition-colors ${isActive ? 'text-blue-600' : 'text-gray-700 hover:text-blue-600'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <button
              onClick={() => navigate('/utility-allowances')}
              className="bg-blue-600 text-white px-4 py-2.5 rounded-full hover:bg-blue-700 transition-all transform hover:scale-105 shadow-lg whitespace-nowrap"
            >
              Utility Charts
            </button>

            <button
              onClick={() => navigate('/translate')}
              className="bg-green-600 text-white px-4 py-2.5 rounded-full hover:bg-green-700 transition-all transform hover:scale-105 shadow-lg whitespace-nowrap"
            >
              Translations
            </button>

            {showLogoutButton ? (
              <button
                onClick={handleLogout}
                className="bg-red-600 text-white px-4 py-2.5 rounded-full hover:bg-red-700 transition-all transform hover:scale-105 shadow-lg flex items-center gap-2 whitespace-nowrap"
              >
                <LogOut className="h-4 w-4" aria-hidden="true" />
                Logout
              </button>
            ) : (
              <button
                onClick={handleDashboardClick}
                className="bg-gray-600 text-white px-4 py-2.5 rounded-full hover:bg-gray-700 transition-all transform hover:scale-105 shadow-lg whitespace-nowrap"
              >
                Dashboard
              </button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            type="button"
            className="lg:hidden p-2 -mr-2 rounded-md text-gray-900"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-menu"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" aria-hidden="true" /> : <Menu className="h-6 w-6" aria-hidden="true" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            id="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t overflow-hidden"
          >
            <div className="container mx-auto px-4 py-4">
              {navItems.map(item => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `block w-full text-left py-3 ${isActive ? 'text-blue-600 font-medium' : 'text-gray-700 hover:text-blue-600'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <button
                onClick={() => {
                  navigate('/utility-allowances');
                  setMobileMenuOpen(false);
                }}
                className="w-full bg-blue-600 text-white px-6 py-3 rounded-full hover:bg-blue-700 transition-all mt-4"
              >
                Utility Charts
              </button>

              <button
                onClick={() => {
                  navigate('/translate');
                  setMobileMenuOpen(false);
                }}
                className="w-full bg-green-600 text-white px-6 py-3 rounded-full hover:bg-green-700 transition-all mt-2"
              >
                Translations
              </button>

              {showLogoutButton ? (
                <button
                  onClick={() => {
                    handleLogout();
                    setMobileMenuOpen(false);
                  }}
                  className="w-full bg-red-600 text-white px-6 py-3 rounded-full hover:bg-red-700 transition-all mt-2 flex items-center justify-center gap-2"
                >
                  <LogOut className="h-4 w-4" aria-hidden="true" />
                  Logout
                </button>
              ) : (
                <button
                  onClick={() => {
                    handleDashboardClick();
                    setMobileMenuOpen(false);
                  }}
                  className="w-full bg-gray-600 text-white px-6 py-3 rounded-full hover:bg-gray-700 transition-all mt-2"
                >
                  Dashboard
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navigation;